import { KPIPicker } from "@/components/kpi/KPIPicker";
import { useUnsentChatGuard } from "@/components/chat/UnsentChatGuard";
import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextField, TextArea, SelectField, DateField } from "@/components/form/Fields";
import { MentionTextarea } from "@/components/form/MentionTextarea";
import { MultiSelect } from "@/components/form/MultiSelect";
import { AssigneePicker } from "@/components/form/AssigneePicker";
import { useOptions, nonPropertyOrgOptions, propertyOptions, contactOptions, userOptions, quickAddOrg, quickAddContact } from "@/components/hooks/useOptions";
import { useAuth } from "@/context/AuthContext";
import client, { formatApiErrorDetail } from "@/lib/api";
import { saveAction } from "@/lib/saveAction";
import { toast } from "sonner";
import { ACTIVITY_TYPES, PRIORITIES } from "@/lib/constants";
import { FollowUpSection } from "@/components/shared/FollowUpSection";
import { useActionCategories } from "@/components/hooks/useActionCategories";
import { usePermissions } from "@/context/PermissionsContext";
import { ActionAttachments, uploadPending } from "@/components/shared/ActionAttachments";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { TaskChatTab } from "@/components/modals/TaskModal";
import { CreatedByNote } from "@/components/shared/CreatedByNote";
import { ModalNotes } from "@/components/shared/ModalNotes";

const todayKey = () => new Date().toLocaleDateString("en-CA");

export function QuickActionModal({ open, prefill = {}, record, onClose, onSuccess }) {
  const [guard, unsentDialog] = useUnsentChatGuard();
  const { user, users } = useAuth();
  const { orgs, contacts, props } = useOptions(open);
  const { subtypes } = useActionCategories();
  const { showSubtypes, can } = usePermissions();
  const followRef = useRef();
  const [form, setForm] = useState(() => {
    const base = record || { ...prefill };
    const orgIds = base.organisation_ids?.length ? base.organisation_ids : (base.organisation_id ? [base.organisation_id] : []);
    const propIds = base.property_ids?.length ? base.property_ids : (base.property_id ? [base.property_id] : []);
    const assigned = base.assigned_to_ids?.length ? base.assigned_to_ids : (base.assigned_to_id ? [base.assigned_to_id] : (user?.id ? [user.id] : []));
    const type = base.activity_type || (base.action_type && base.action_type !== "Task" ? base.action_type : "Task");
    return { ...base, sub_types: base.sub_types?.length ? base.sub_types : (base.sub_type ? [base.sub_type] : []), activity_type: type, organisation_ids: orgIds, property_ids: propIds, assigned_to_ids: assigned,
      activity_date: (base.completed_date || todayKey()).slice(0, 10) };
  });
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [tab, setTab] = useState("details");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isEdit = record && record.id;

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const { activity_date, ...rest } = form;
      const body = { ...rest, action_type: form.activity_type, status: "Completed", completed_date: activity_date,
        due_date: activity_date, assigned_to_id: null, property_id: null };
      if (isEdit) delete body.notes;
      const data = isEdit ? await saveAction(record, body) : (await client.post("/actions", body)).data;
      if (!isEdit && pendingFiles.length) { await uploadPending("action", data.id, pendingFiles); setPendingFiles([]); }
      await followRef.current?.submitIfPending();
      toast.success(`Activity ${isEdit ? "updated" : "logged"}`);
      onSuccess && onSuccess(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && guard(onClose)()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="quick-action-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" /> {isEdit ? "Edit Activity" : "Log Activity"}
          </DialogTitle>
          <DialogDescription className="sr-only">Log or edit a completed activity</DialogDescription>
        </DialogHeader>

        {isEdit && (
          <div className="flex items-center gap-1 border-b border-slate-100 pb-2">
            {[["details", "Details"], ["chat", "Chat"]].map(([val, label]) => (
              <button key={val} onClick={() => setTab(val)} data-testid={`activity-tab-${val}`}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${tab === val ? "bg-sky-50 text-sky-700" : "text-slate-500 hover:bg-slate-50"}`}>
                {val === "chat" && <MessageCircle className="h-4 w-4" />}{label}
              </button>
            ))}
          </div>
        )}

        {isEdit && tab === "chat" ? (
          <div className="py-2"><TaskChatTab taskId={record.id} /></div>
        ) : (
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <TextField label="Title" required value={form.title} onChange={set("title")} testid="action-field-title" placeholder="e.g. Call James about 2027 campaign" />
            </div>
            <SelectField label="Type" value={form.activity_type} onChange={set("activity_type")} options={ACTIVITY_TYPES} allowEmpty={false} testid="action-field-type" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-600">Assigned To</label>
              <AssigneePicker multi value={form.assigned_to_ids || []} onChange={set("assigned_to_ids")} users={users} testid="action-field-assignee" placeholder="Assign users" />
            </div>
            <DateField label="Activity Date" value={form.activity_date} onChange={set("activity_date")} testid="action-field-date" />
          </div>
          {can("kpis", "view") && (
            <KPIPicker value={form.kpi_ids || []} onChange={set("kpi_ids")} userIds={form.assigned_to_ids || []} testid="action-field-kpis" />
          )}

          <div className="space-y-3 rounded-lg bg-slate-50 p-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Links (optional)</p>
            <MultiSelect propertyField label="Property" value={form.property_ids || []} onChange={set("property_ids")} options={propertyOptions(props)} testid="action-field-property" placeholder="Link properties…" />
            <MultiSelect label="Organisations" value={form.organisation_ids || []} onChange={set("organisation_ids")} options={nonPropertyOrgOptions(orgs)} onQuickAdd={quickAddOrg} testid="action-field-org" placeholder="Link organisations…" />
            <MultiSelect label="Contacts" value={form.contact_ids || []} onChange={set("contact_ids")} options={contactOptions(contacts)} onQuickAdd={quickAddContact} testid="action-field-contacts" placeholder="Link contacts…" />
          </div>

          <ModalNotes entityType="action" collection="actions" record={record} value={form.notes} onChange={set("notes")} testid="action-field-notes" />
          <ActionAttachments recordId={isEdit ? record.id : null} files={pendingFiles} setFiles={setPendingFiles} />

          <FollowUpSection ref={followRef} parent={form} orgs={orgs} contacts={contacts} users={users} currentUserId={user?.id} />
        </div>
        )}

        {unsentDialog}
        {tab === "details" && (
        <DialogFooter>
          {isEdit && <CreatedByNote record={record} />}
          <Button variant="ghost" onClick={guard(onClose)}>Cancel</Button>
          <Button onClick={guard(save)} disabled={saving} data-testid="action-save-button" className="bg-sky-600 hover:bg-sky-700">
            {saving ? "Saving…" : "Save Activity"}
          </Button>
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CompleteActionModal({ open, action, onClose, onSuccess }) {
  const { users } = useAuth();
  const [notes, setNotes] = useState("");
  const [createNext, setCreateNext] = useState(false);
  const [next, setNext] = useState({ action_type: "Task", priority: "Medium" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setNotes(action?.notes || "");
      setCreateNext(false);
      setNext({ action_type: "Task", priority: "Medium" });
    }
  }, [open, action]);

  const setN = (k) => (v) => setNext((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { notes };
      if (createNext && next.title) payload.next_action = next;
      await client.post(`/actions/${action.id}/complete`, payload);
      toast.success("Action completed");
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!action) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="complete-action-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" /> Complete Action
          </DialogTitle>
          <DialogDescription className="sr-only">Add notes and optionally create a follow-up</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <p className="text-sm font-semibold text-slate-800">{action.title}</p>
            <p className="text-xs text-slate-500">
              {action.activity_type || action.action_type}{(action.sub_types?.length ? action.sub_types : action.sub_type ? [action.sub_type] : []).map((t) => ` · ${t}`).join("")}
              {action.organisation_name ? ` · ${action.organisation_name}` : ""}
            </p>
          </div>
          <TextArea label="Notes" value={notes} onChange={setNotes} testid="complete-notes" />

          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              data-testid="complete-create-next"
              checked={createNext}
              onChange={(e) => setCreateNext(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
            />
            Create Follow Up
          </label>

          {createNext && (
            <div className="rounded-lg bg-sky-50/60 p-3 space-y-3 border border-sky-100">
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="Type" value={next.action_type} onChange={setN("action_type")} options={ACTIVITY_TYPES} allowEmpty={false} />
                <SelectField label="Priority" value={next.priority} onChange={setN("priority")} options={PRIORITIES} allowEmpty={false} />
              </div>
              <TextField label="Description" value={next.title} onChange={setN("title")} testid="next-action-title" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">Assigned To</label>
                  <AssigneePicker multi={false} value={next.assigned_to_id} onChange={setN("assigned_to_id")} users={users} testid="next-action-assignee" placeholder="Assign" />
                </div>
                <DateField label="Due Date" value={next.due_date} onChange={setN("due_date")} />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="complete-submit-button" className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? "Completing…" : "Complete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
